import { useState, FormEvent } from 'react';

export default function ConsultationForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    trainingType: '',
    teamSize: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5t4b4mm4hfebckkvmj0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          trainingType: '',
          teamSize: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="consultation" className="bg-black py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Info */}
          <div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4C4B0] font-light mb-6">
              Get In Touch
            </div>
            <h2 className="text-[40px] sm:text-[48px] md:text-[56px] font-bold text-white mb-6 leading-tight font-serif">
              Request Consultation
            </h2>
            <p className="text-[16px] md:text-[18px] text-[#C0C0C0] mb-12 leading-relaxed">
              concierge@ufirstdoctorselite.com
            </p>
          </div>

          {/* Right Column - Form */}
          <div className="bg-[#1A1A1A] rounded-2xl p-8 md:p-10">
            <form id="consultation-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-[14px] font-medium text-white mb-2">
                    Full Name <span className="text-[#D4C4B0]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black border border-[#333333] rounded-lg text-[15px] text-white placeholder-[#666666] focus:outline-none focus:border-[#D4C4B0] transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[14px] font-medium text-white mb-2">
                    Email Address <span className="text-[#D4C4B0]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black border border-[#333333] rounded-lg text-[15px] text-white placeholder-[#666666] focus:outline-none focus:border-[#D4C4B0] transition-colors"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-[14px] font-medium text-white mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-black border border-[#333333] rounded-lg text-[15px] text-white placeholder-[#666666] focus:outline-none focus:border-[#D4C4B0] transition-colors"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="organization" className="block text-[14px] font-medium text-white mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-black border border-[#333333] rounded-lg text-[15px] text-white placeholder-[#666666] focus:outline-none focus:border-[#D4C4B0] transition-colors"
                    placeholder="Company Name"
                  />
                </div>

                <div>
                  <label htmlFor="trainingType" className="block text-[14px] font-medium text-white mb-2">
                    Training Type <span className="text-[#D4C4B0]">*</span>
                  </label>
                  <select
                    id="trainingType"
                    name="trainingType"
                    value={formData.trainingType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black border border-[#333333] rounded-lg text-[15px] text-white focus:outline-none focus:border-[#D4C4B0] transition-colors cursor-pointer"
                  >
                    <option value="">Select training type</option>
                    <option value="Customized to your needs">Customized to your needs</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="teamSize" className="block text-[14px] font-medium text-white mb-2">
                    Team Size
                  </label>
                  <select
                    id="teamSize"
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-black border border-[#333333] rounded-lg text-[15px] text-white focus:outline-none focus:border-[#D4C4B0] transition-colors cursor-pointer"
                  >
                    <option value="">Select team size</option>
                    <option value="1-5">1-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="11-20">11-20 people</option>
                    <option value="21-50">21-50 people</option>
                    <option value="50+">50+ people</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-[14px] font-medium text-white mb-2">
                    Message <span className="text-[#D4C4B0]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-black border border-[#333333] rounded-lg text-[15px] text-white placeholder-[#666666] focus:outline-none focus:border-[#D4C4B0] transition-colors resize-none"
                    placeholder="Tell us about your training needs..."
                  />
                  <div className="text-[12px] text-[#666666] mt-1 text-right">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-[#D4C4B0] text-black text-[15px] font-semibold rounded-lg hover:bg-[#C4B4A0] transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Request'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-[14px] text-green-400 text-center">
                      Thank you! We'll get back to you within 24 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-[14px] text-red-400 text-center">
                      Something went wrong. Please try again or email us directly.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
